import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Message,
  Modal,
  Radio,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "@arco-design/web-react";
import { IconDelete, IconPlus } from "@arco-design/web-react/icon";
import Content from "../Layout/Content";
import SummaryCard from "../Layout/SummaryCard";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import { FetchApi, PostApi, DeleteApi } from "../Api/AuthenticatedApi";

const { Text } = Typography;

// The create dialog loads the pickers (pipelines / lists / segments / members)
// lazily when opened, and posts a workflow whose audience is exactly one of a
// contact list or a segment.
function CreateWorkflowModal({ visible, onClose, onCreated }) {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const [pipelines, setPipelines] = useState([]);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [members, setMembers] = useState([]);

  const [name, setName] = useState("");
  const [pipelineId, setPipelineId] = useState(undefined);
  const [audienceType, setAudienceType] = useState("list");
  const [listId, setListId] = useState(undefined);
  const [segmentId, setSegmentId] = useState(undefined);
  const [assignees, setAssignees] = useState([]);
  const [trackValue, setTrackValue] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const load = (url, setter, pick) =>
      FetchApi(getAccessTokenSilently, url,
        (data) => setter(pick ? pick(data) : data || []),
        () => navigate("/login"),
        () => {});
    load("/api/pipelines/", setPipelines);
    load("/api/contact-lists/", setLists);
    load("/api/segments/", setSegments);
    load("/api/members/", setMembers);
  }, [visible, getAccessTokenSilently, navigate]);

  const reset = () => {
    setName(""); setPipelineId(undefined); setAudienceType("list");
    setListId(undefined); setSegmentId(undefined); setAssignees([]); setTrackValue(false); setSaving(false);
  };

  const close = () => { reset(); onClose(); };

  const save = () => {
    if (!name.trim()) { Message.warning("Name your workflow"); return; }
    if (!pipelineId) { Message.warning("Choose a pipeline"); return; }
    const audienceId = audienceType === "list" ? listId : segmentId;
    if (!audienceId) { Message.warning("Choose an audience"); return; }

    // Build the body with only the chosen audience key (never send "" for a UUID).
    const body = { name: name.trim(), pipeline_id: pipelineId, assignee_user_ids: assignees, track_value: trackValue };
    if (audienceType === "list") body.contact_list_id = listId;
    else body.segment_id = segmentId;

    setSaving(true);
    PostApi(getAccessTokenSilently, "/api/workflows/",
      () => { Message.success("Workflow created"); close(); onCreated(); },
      () => navigate("/login"),
      (err) => { setSaving(false); Message.error((err && err.message) || "Failed to create workflow"); },
      body);
  };

  return (
    <Modal
      title="New workflow"
      visible={visible}
      onCancel={close}
      footer={
        <Space>
          <Button onClick={close}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={save}>Create</Button>
        </Space>
      }
      style={{ width: 560 }}
      unmountOnExit
    >
      <Space direction="vertical" size={14} style={{ width: "100%" }}>
        <div>
          <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Name</Text></div>
          <Input placeholder="e.g. Q3 Enterprise Sales" value={name} onChange={setName} />
        </div>

        <div>
          <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Pipeline</Text></div>
          <Select
            placeholder="Choose a pipeline"
            value={pipelineId}
            onChange={setPipelineId}
            options={pipelines.map((p) => ({ label: p.name, value: p.id }))}
            notFoundContent={<span style={{ color: "var(--color-text-3)" }}>No pipelines — create one in Settings → Pipelines</span>}
          />
        </div>

        <div>
          <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Audience</Text></div>
          <Radio.Group
            type="button"
            value={audienceType}
            onChange={setAudienceType}
            style={{ marginBottom: 8 }}
            options={[{ label: "Contact list", value: "list" }, { label: "Segment", value: "segment" }]}
          />
          {audienceType === "list" ? (
            <Select
              placeholder="Choose a contact list"
              value={listId}
              onChange={setListId}
              options={lists.map((l) => ({ label: l.name, value: l.id }))}
            />
          ) : (
            <Select
              placeholder="Choose a segment"
              value={segmentId}
              onChange={setSegmentId}
              options={segments.map((s) => ({ label: s.name, value: s.id }))}
            />
          )}
        </div>

        <div>
          <div style={{ marginBottom: 4 }}><Text style={{ fontWeight: 500 }}>Assignees</Text></div>
          <Select
            mode="multiple"
            placeholder="Who works this board?"
            value={assignees}
            onChange={setAssignees}
            options={members.map((m) => ({ label: m.name || m.email, value: m.id }))}
          />
        </div>

        <Checkbox checked={trackValue} onChange={setTrackValue}>
          Track deal value (amounts &amp; forecast)
        </Checkbox>
      </Space>
    </Modal>
  );
}

const audienceLabel = (w) => {
  if (w.contact_list_id) return "List";
  if (w.segment_id) return "Segment";
  return "—";
};

export default function Workflows() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchWorkflows = () => {
    setLoading(true);
    FetchApi(getAccessTokenSilently, "/api/workflows/",
      (data) => { setRows(data || []); setLoading(false); },
      () => navigate("/login"),
      () => { setLoading(false); Message.error("Failed to load workflows"); });
  };

  useEffect(fetchWorkflows, [getAccessTokenSilently, navigate]);

  const remove = (e, w) => {
    e.stopPropagation();
    Modal.confirm({
      title: "Delete workflow",
      content: `Delete "${w.name}"? Its cards and history will be removed.`,
      okButtonProps: { status: "danger" },
      onOk: () =>
        new Promise((resolve) => {
          DeleteApi(getAccessTokenSilently, "/api/workflows/" + w.id,
            () => { Message.success("Workflow deleted"); fetchWorkflows(); resolve(); },
            () => navigate("/login"),
            () => { Message.error("Failed to delete workflow"); resolve(); },
            {});
        }),
    });
  };

  const columns = [
    { title: "Name", dataIndex: "name", render: (n) => <Text style={{ fontWeight: 500 }}>{n}</Text> },
    { title: "Pipeline", dataIndex: "pipeline_name", render: (n) => n || <Text type="secondary">—</Text> },
    { title: "Audience", render: (_, w) => <Tag size="small">{audienceLabel(w)}</Tag> },
    { title: "Assignees", render: (_, w) => (w.assignee_user_ids || []).length },
    { title: "Cards", dataIndex: "card_count" },
    {
      title: "",
      width: 48,
      render: (_, w) => (
        <Button size="small" type="text" shape="circle" icon={<IconDelete />} onClick={(e) => remove(e, w)} />
      ),
    },
  ];

  return (
    <Content>
      <SimpleBreadcrumbs elements={[{ name: "Workflows" }]} />

      <div style={{ display: "flex", justifyContent: "flex-end", padding: "8px 8px" }}>
        <Button type="primary" icon={<IconPlus />} onClick={() => setModalOpen(true)}>
          New workflow
        </Button>
      </div>

      <SummaryCard title="Workflows" subTitle="Move contacts through the stages of a pipeline">
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          data={rows}
          loading={loading}
          pagination={false}
          noDataElement={
            <Empty description="No workflows yet">
              <Button type="primary" style={{ marginTop: 12 }} onClick={() => setModalOpen(true)}>
                Create your first workflow
              </Button>
            </Empty>
          }
          onRow={(row) => ({
            style: { cursor: "pointer" },
            onClick: () => navigate(`/workflows/${row.id}`),
          })}
        />
      </SummaryCard>

      <CreateWorkflowModal
        visible={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={fetchWorkflows}
      />
    </Content>
  );
}
